
import { defineComponent } from 'vue';
import { loadMoov } from '@moovio/moov-js';
import PageLoader from '@/components/common/PageLoader.vue';
import CustomButton from '~/components/Buttons/CustomButton.vue';
import MoovInputWrapper from '@/components/common/Inputs/MoovInputWrapper.vue';
import { MoovDropInputProperties, MoovFormDrop } from '@/interfaces/moov';
import { CardPaymentOption, MoovCard, MoovCardTypes } from '@/interfaces/payment-options';
import { syncCards } from '@/api/payment-options';
import { OnboardingDetails } from '@/interfaces/onboarding';

export default defineComponent({
  name: 'CardInput',

  components: { MoovInputWrapper, PageLoader, CustomButton },

  data: () => ({
    moovInitialized: false,
    isFormValid: false,
    loadingCard: false,
    creditCardError: false,
  }),

  async mounted() {
    await this.bootstrapMoov();
  },

  computed: {
    inputStyles() {
      return {
        fontSize: '16px',
        lineHeight: '20px',
      };
    },

    zipInputProps(): Partial<MoovDropInputProperties> {
      return {
        minLength: 5,
        maxLength: 5,
      };
    },

    moovTokenDetails(): OnboardingDetails {
      return this.$store.getters['PaymentOptions/getOnboardingDetails'];
    }
  },

  methods: {
    async bootstrapMoov() {
      if (!this.moovInitialized) {
        await this.$store.dispatch('PaymentOptions/fetchMoovDropToken');

        await loadMoov(this.moovTokenDetails.oauth_token as string);
        this.moovInitialized = true;
        await this.$nextTick();

        const form = this.$refs.moovForm as MoovFormDrop;
        form.method = 'POST';
        form.action = `/accounts/${this.moovTokenDetails.account_id}/cards`;
        form.requestHeaders = {
          'content-type': 'application/json',
          Authorization: `Bearer ${this.moovTokenDetails.oauth_token}`,
        };

        form.onCheckValidity = (isValid) => {
          this.isFormValid = isValid;
        };

        form.onSuccess = async (response) => {
          const moovCard = await response.json() as MoovCard;

          if (moovCard.cardType === MoovCardTypes.CREDIT) {
            this.setErrorState();
            return;
          }

          try {
            const newCards = await this.validateCardAddSuccess(moovCard);
            this.$emit('success', newCards);
          } catch {
            this.setErrorState();
          } finally {
            this.loadingCard = false;
          }
        };

        form.onError = () => {
          this.setErrorState();
        };
      }
    },

    validateForm() {
      (this.$refs.moovForm as MoovFormDrop).checkValidity();
    },

    submitCard() {
      const form = this.$refs.moovForm as MoovFormDrop;
      form.requestSubmit();

      if (this.isFormValid) {
        this.loadingCard = true;
      }
    },

    /**
     * This is used to double-check if the card was added successfully
     * There are some cases when cardType is 'unknown' for a short period of time after submit
     * This re-checks if the cardType has been updated to a type we can use
     * @param moovCard
     * @private
     */
    async validateCardAddSuccess(moovCard: MoovCard): Promise<CardPaymentOption> {
      const { data: newCards } = await syncCards();
      const entries = Object.entries(newCards);
      const newCard = {
        id: entries[0][0],
        ...entries[0][1]
      };

      if (!newCard || newCard.id !== moovCard.cardID) {
        throw new Error('Card not added');
      }

      return Promise.resolve(newCards);
    },

    setErrorState() {
      this.loadingCard = false;
      this.creditCardError = true;
    }
  }
});
